<script>
  import { router } from '@inertiajs/svelte'

  let { report = {}, target = {}, related_reports = [], flash = {} } = $props()

  let actionNote = $state('')
  let showActionModal = $state(false)
  let currentAction = $state('')

  function openAction(action) {
    currentAction = action
    actionNote = ''
    showActionModal = true
  }

  function executeAction() {
    router.patch(`/admin/reports/${report.id}`, {
      action_type: currentAction,
      note: actionNote
    })
    showActionModal = false
  }

  function getStatusBadge(status) {
    switch (status) {
      case 'pending': return 'badge-warning'
      case 'processing': return 'badge-info'
      case 'resolved': return 'badge-success'
      case 'dismissed': return 'badge-neutral'
      default: return 'badge-neutral'
    }
  }

  function getReasonLabel(reason) {
    const labels = {
      spam_commercial:    '반복 홍보, 상업적 게시물',
      personal_info:      '개인정보 노출 (실명·연락처·계좌번호 등)',
      illegal_content:    '불법 콘텐츠 (마약/도박/저작권 침해)',
      csam:               '아동·청소년 성착취물(CSAM)',
      adult_content:      '성인물, 선정적 콘텐츠',
      hate_harassment:    '차별/모욕/따돌림/괴롭힘',
      violence_self_harm: '폭력 위협, 자해/자살 관련 위험 발언',
      false_info:         '허위 정보 (채용 사기, 잘못된 기술 정보)',
      other:              '기타 (위 사유에 해당하지 않는 경우)'
    }
    return labels[reason] || reason
  }

  function getUrgencyBadge(urgency) {
    switch (urgency) {
      case 'critical': return 'badge-error'
      case 'high':     return 'badge-warning'
      default:         return 'badge-neutral'
    }
  }

  function getUrgencyLabel(urgency) {
    const map = { critical: '긴급', high: '높음', normal: '보통' }
    return map[urgency] || urgency
  }

  function getSlaLabel(urgency) {
    const map = { critical: '24시간 내 처리', high: '72시간 내 처리', normal: '7일 내 처리' }
    return map[urgency] || '-'
  }

  function getActionLabel(action) {
    switch (action) {
      case 'approve': return '신고 승인 (콘텐츠 숨김)'
      case 'reject': return '신고 기각'
      case 'warning': return '경고 발송'
      case 'dismiss': return '신고 무시'
      default: return action
    }
  }

  function getActionColor(action) {
    switch (action) {
      case 'approve': return 'btn-danger'
      case 'warning': return 'btn-secondary'
      case 'reject': return 'btn-secondary'
      case 'dismiss': return 'btn-secondary'
      default: return 'btn-secondary'
    }
  }
</script>

<!-- Breadcrumb -->
  <div class="flex items-center gap-2 text-caption text-content-tertiary mb-6">
    <a href="/admin" class="hover:text-primary transition-colors">관리자</a>
    <span>/</span>
    <a href="/admin/reports" class="hover:text-primary transition-colors">신고</a>
    <span>/</span>
    <span class="text-content">신고 상세</span>
  </div>

  <main class="max-w-4xl">
    {#if flash.notice}
      <div class="mb-6 p-4 bg-success-light dark:bg-success/20 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2">
        {flash.notice}
      </div>
    {/if}

    <!-- 긴급 처리 배너 -->
    {#if report.urgency_level === 'critical'}
      <div class="mb-4 p-4 bg-error/10 border border-error/50 rounded-xl flex items-center gap-3">
        <svg class="w-6 h-6 text-error shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
        </svg>
        <div>
          <span class="text-error font-bold text-body1">⚠ 긴급 처리 필요</span>
          <span class="text-error text-body2 ml-3">
            24시간 내 처리 필요 — 청소년성보호법 §17 / 정보통신망법 §44 적용 대상
          </span>
        </div>
      </div>
    {:else if report.urgency_level === 'high'}
      <div class="mb-4 p-3 bg-warning-light dark:bg-warning/10 border border-warning/30 rounded-xl flex items-center gap-2">
        <svg class="w-5 h-5 text-warning-dark dark:text-warning shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <span class="text-warning-dark dark:text-warning text-body2 font-medium">
          72시간 내 처리 권장
        </span>
      </div>
    {/if}

    <!-- Report Info -->
    <div class="card card-bordered overflow-hidden mb-6 shadow-md">
      <div class="p-6 border-b border-default">
        <div class="flex items-start justify-between">
          <div>
            <div class="flex items-center gap-2 mb-2">
              <span class={`badge ${getStatusBadge(report.status)}`}>{report.status}</span>
              <span class="badge badge-neutral">{report.target_type}</span>
              {#if report.urgency_level}
                <span class={`badge ${getUrgencyBadge(report.urgency_level)}`}>
                  {getUrgencyLabel(report.urgency_level)}
                </span>
                <span class="text-caption text-gray-400 dark:text-dark-text-disabled">
                  ({getSlaLabel(report.urgency_level)})
                </span>
              {/if}
            </div>
            <h1 class={`text-h1 ${report.urgency_level === 'critical' ? 'text-error' : 'text-content'}`}>
              {report.reason_label || getReasonLabel(report.reason)}
            </h1>
          </div>

          {#if report.status === 'pending' || report.status === 'processing'}
            <div class="flex items-center gap-2">
              <button onclick={() => openAction('approve')} class="btn btn-danger btn-sm">승인 (숨김)</button>
              <button onclick={() => openAction('warning')} class="btn btn-secondary btn-sm">경고</button>
              <button onclick={() => openAction('dismiss')} class="btn btn-secondary btn-sm">기각</button>
            </div>
          {/if}
        </div>
      </div>

      <!-- Report Details -->
      <div class="p-6 space-y-4">
        <div class="grid md:grid-cols-2 gap-6">
          <dl class="space-y-3">
            <div class="flex justify-between">
              <dt class="text-content-tertiary">신고자</dt>
              <dd class="text-content font-medium">{report.reporter?.name || '익명'}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">신고일</dt>
              <dd class="text-content">{new Date(report.created_at).toLocaleString('ko-KR')}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">대상 유형</dt>
              <dd class="text-content">{report.target_type}</dd>
            </div>
          </dl>
          <dl class="space-y-3">
            {#if report.ai_verdict}
              <div class="flex justify-between">
                <dt class="text-content-tertiary">AI 판정</dt>
                <dd>
                  <span class={`badge ${report.ai_verdict === 'safe' ? 'badge-success' : report.ai_verdict === 'unsafe' ? 'badge-error' : 'badge-warning'}`}>
                    {report.ai_verdict}
                  </span>
                </dd>
              </div>
            {/if}
            {#if report.ai_score}
              <div class="flex justify-between">
                <dt class="text-content-tertiary">AI 점수</dt>
                <dd class="text-content font-mono">{report.ai_score}</dd>
              </div>
            {/if}
            {#if report.processed_at}
              <div class="flex justify-between">
                <dt class="text-content-tertiary">처리일</dt>
                <dd class="text-content">{new Date(report.processed_at).toLocaleString('ko-KR')}</dd>
              </div>
            {/if}
          </dl>
        </div>

        {#if report.description}
          <div class="mt-4 p-4 bg-surface-tertiary rounded-xl">
            <h3 class="text-label text-content-tertiary uppercase mb-2">신고 상세 내용</h3>
            <p class="text-body2 text-content whitespace-pre-wrap">{report.description}</p>
          </div>
        {/if}

        {#if report.admin_note}
          <div class="mt-4 p-4 bg-primary-50 dark:bg-primary/10 rounded-xl">
            <h3 class="text-label text-primary uppercase mb-2">관리자 메모</h3>
            <p class="text-body2 text-content">{report.admin_note}</p>
          </div>
        {/if}
      </div>
    </div>

    <!-- Target Content -->
    {#if target}
      <div class="card card-bordered overflow-hidden mb-6 shadow-md">
        <div class="px-6 py-4 border-b border-default">
          <h2 class="text-title text-content">신고 대상 콘텐츠</h2>
        </div>
        <div class="p-6">
          {#if target.title}
            <h3 class="text-body1 font-semibold text-content mb-2">{target.title}</h3>
          {/if}
          {#if target.content}
            <div class="text-body2 text-gray-700 dark:text-dark-text-medium whitespace-pre-wrap bg-surface-tertiary p-4 rounded-xl border border-default">
              {target.content?.substring(0, 500)}{target.content?.length > 500 ? '...' : ''}
            </div>
          {/if}
          {#if target.user}
            <div class="mt-3 text-caption text-content-tertiary">
              작성자: {target.user?.name || '익명'} | {target.user?.organization_name || '-'}
            </div>
          {/if}
        </div>
      </div>
    {/if}

    <!-- Related Reports -->
    {#if related_reports.length > 0}
      <div class="card card-bordered overflow-hidden mb-6 shadow-md">
        <div class="px-6 py-4 border-b border-default">
          <h2 class="text-title text-content">동일 대상 관련 신고 ({related_reports.length}건)</h2>
        </div>
        <div class="divide-y divide-gray-100 dark:divide-dark-border">
          {#each related_reports as r}
            <div class="px-6 py-4 flex items-center justify-between">
              <div>
                <span class="text-body1 font-medium text-content">{getReasonLabel(r.reason)}</span>
                <span class="text-caption text-content-tertiary ml-2">by {r.reporter?.name || '익명'}</span>
              </div>
              <div class="flex items-center gap-2">
                {#if r.urgency_level}
                  <span class={`badge ${getUrgencyBadge(r.urgency_level)}`}>{getUrgencyLabel(r.urgency_level)}</span>
                {/if}
                <span class={`badge ${getStatusBadge(r.status)}`}>{r.status}</span>
                <span class="text-caption text-content-tertiary">{new Date(r.created_at).toLocaleDateString('ko-KR')}</span>
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/if}

    <!-- Back -->
    <div class="flex justify-center mt-8">
      <a href="/admin/reports" class="btn btn-secondary btn-md">목록으로</a>
    </div>
  </main>

  <!-- Action Modal -->
  {#if showActionModal}
    <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="card card-bordered p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 class="text-title text-content mb-2">{getActionLabel(currentAction)}</h3>
        <p class="text-body2 text-content-secondary mb-4">처리 사유를 입력해주세요.</p>

        <textarea
          bind:value={actionNote}
          rows="3"
          placeholder="처리 사유..."
          class="textarea resize-none mb-4"
        ></textarea>

        <div class="flex justify-end gap-3">
          <button onclick={() => showActionModal = false} class="btn btn-secondary btn-sm">취소</button>
          <button onclick={executeAction} class={`btn ${getActionColor(currentAction)} btn-sm`}>
            확인
          </button>
        </div>
      </div>
    </div>
  {/if}
